
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { resolveFrom } from '../../utils/safeRedirect';

interface ProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
  requireAuth?: boolean;
  requireAdmin?: boolean;
  requireBroker?: boolean;
}

const BROKER_ROLES = ['BROKER', 'AGENT', 'COMPANY_ADMIN', 'ADMIN', 'SUPER_ADMIN'];

export default function ProtectedRoute({
  children,
  redirectTo = '/signin',
  requireAuth = true,
  requireAdmin = false,
  requireBroker = false
}: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { t, i18n } = useTranslation('common');

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" dir={i18n.dir()}>
        <div className="text-center space-y-2">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-800 font-medium">{t('auth.loadingTitle')}</p>
          <p className="text-sm text-gray-500">{t('auth.loadingSubtitle')}</p>
        </div>
      </div>
    );
  }

  // If route requires authentication and user is not logged in
  if (requireAuth && !user) {
    // Save the attempted location for redirect after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // If route requires admin role
  if (requireAdmin && user) {
    const isAdmin = user.role === 'ADMIN' || user.role === 'SUPER_ADMIN';
    if (!isAdmin) {
      return <Navigate to="/" replace />;
    }
  }

  // If route requires a broker-tier account (listing properties, broker workflows)
  if (requireBroker && user) {
    const isBroker = typeof user.role === 'string' && BROKER_ROLES.includes(user.role);
    if (!isBroker) {
      // Individuals don't use the broker listing form — send them to the
      // platform-FAL listing wizard (their listing path), not the now
      // broker/company-only subscriptions page.
      return <Navigate to="/individual-property-request" replace />;
    }
  }

  // If route requires no authentication (like signin/signup) and user is logged in
  if (!requireAuth && user) {
    // Redirect to intended destination or home. resolveFrom reads the same
    // return address the sign-in page does (state.from as a path or Location,
    // then ?redirect= / ?returnUrl=), keeps its query and hash, and only
    // allows paths on this site.
    const from = resolveFrom(location.state, location.search);
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}

